/**
 * Settings Page
 * Manage business profile, notifications, and integrations
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Input, toast } from '../components/ui';
import { businessApi, squareApi, SquareIntegration, LoyaltySyncStatus, LoyaltySyncHealth } from '../lib/api';

interface BusinessProfile {
  businessName: string;
  category: string;
  phone: string;
  email: string;
  website: string;
  description: string;
  logo: string;
}

interface NotificationSettings {
  emailNewCustomer: boolean;
  emailDailyReport: boolean;
  emailWeeklyReport: boolean;
  emailLowStars: boolean;
  smsNewTransaction: boolean;
  smsLargeRedemption: boolean;
}

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'integrations' | 'rewards' | 'customer-data'>('profile');
  const [isSaving, setIsSaving] = useState(false);

  const [profile, setProfile] = useState<BusinessProfile>({
    businessName: '',
    category: 'CAFE',
    phone: '',
    email: '',
    website: '',
    description: '',
    logo: '',
  });

  useEffect(() => {
    businessApi.getProfile()
      .then((res) => {
        const data = res.data.data;
        setProfile({
          businessName: data.businessName || '',
          category: data.category || 'CAFE',
          phone: data.phoneNumber || '',
          email: (data as any).email || '',
          website: data.website || '',
          description: data.description || '',
          logo: data.logo || '',
        });
      })
      .catch(() => {
        // Use empty defaults if profile load fails
      });
  }, []);

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNewCustomer: true,
    emailDailyReport: false,
    emailWeeklyReport: true,
    emailLowStars: true,
    smsNewTransaction: false,
    smsLargeRedemption: true,
  });

  // Reward customization settings
  const [rewardsConfig, setRewardsConfig] = useState({
    b3trPerDollar: 10,
    redemptionMinimum: 100,
    pointExpiration: 365,
    sustainabilityMultiplier: 1.5,
    referralBonus: 50,
  });

  // Customer data collection preferences
  const [customerDataFields, setCustomerDataFields] = useState([
    { id: 'email', name: 'Email Address', required: true, enabled: true, locked: true },
    { id: 'phone', name: 'Phone Number', required: true, enabled: true, locked: true },
    { id: 'firstName', name: 'First Name', required: false, enabled: true, locked: false },
    { id: 'lastName', name: 'Last Name', required: false, enabled: true, locked: false },
    { id: 'birthday', name: 'Birthday', required: false, enabled: true, locked: false },
    { id: 'dietaryRestrictions', name: 'Dietary Restrictions', required: false, enabled: false, locked: false },
    { id: 'favoriteItems', name: 'Favorite Menu Items', required: false, enabled: false, locked: false },
    { id: 'allergies', name: 'Allergies', required: false, enabled: false, locked: false },
  ]);

  const [customFields, setCustomFields] = useState<Array<{ id: string; name: string; required: boolean }>>([]);

  // Square integration state
  const [squareStatus, setSquareStatus] = useState<SquareIntegration>({ connected: false });
  const [squareLoading, setSquareLoading] = useState(false);
  const [squareConnecting, setSquareConnecting] = useState(false);
  const [squareSyncing, setSquareSyncing] = useState(false);

  // Square Loyalty state
  const [loyaltyStatus, setLoyaltyStatus] = useState<LoyaltySyncStatus | null>(null);
  const [loyaltyHealth, setLoyaltyHealth] = useState<LoyaltySyncHealth | null>(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyEnabling, setLoyaltyEnabling] = useState(false);
  const [loyaltySyncing, setLoyaltySyncing] = useState(false);
  const [showSyncHealth, setShowSyncHealth] = useState(false);

  const fetchSquareStatus = useCallback(async () => {
    setSquareLoading(true);
    try {
      const res = await squareApi.getStatus();
      setSquareStatus(res.data.data);
    } catch {
      // Not connected or error fetching - leave as disconnected
    } finally {
      setSquareLoading(false);
    }
  }, []);

  const fetchLoyaltyStatus = useCallback(async () => {
    setLoyaltyLoading(true);
    try {
      const res = await squareApi.getLoyaltySyncStatus();
      setLoyaltyStatus(res.data.data);
    } catch {
      // Not set up yet
    } finally {
      setLoyaltyLoading(false);
    }
  }, []);

  const fetchLoyaltyHealth = useCallback(async () => {
    try {
      const res = await squareApi.getLoyaltySyncHealth();
      setLoyaltyHealth(res.data.data);
    } catch {
      // Health data unavailable
    }
  }, []);

  const handleEnableLoyaltySync = async () => {
    setLoyaltyEnabling(true);
    try {
      const res = await squareApi.enableLoyaltySync();
      setLoyaltyStatus(res.data.data);
      toast.success('Square Loyalty sync enabled successfully!');
      fetchLoyaltyHealth();
    } catch {
      toast.error('Failed to enable loyalty sync. Make sure you have a loyalty program created in Square Dashboard first.');
    } finally {
      setLoyaltyEnabling(false);
    }
  };

  const handleSyncLoyaltyCustomers = async () => {
    setLoyaltySyncing(true);
    try {
      const res = await squareApi.syncLoyaltyCustomers();
      const { syncedCount, errors } = res.data.data;
      if (errors && errors.length > 0) {
        toast.warning(`Synced ${syncedCount} customers with ${errors.length} errors.`);
      } else {
        toast.success(`Successfully synced ${syncedCount} customers!`);
      }
      fetchLoyaltyStatus();
      fetchLoyaltyHealth();
    } catch {
      toast.error('Failed to sync loyalty customers.');
    } finally {
      setLoyaltySyncing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'integrations') {
      fetchSquareStatus();
      fetchLoyaltyStatus();
      fetchLoyaltyHealth();
    }
  }, [activeTab, fetchSquareStatus, fetchLoyaltyStatus, fetchLoyaltyHealth]);

  // Check for OAuth callback results in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const squareResult = params.get('square');
    const tab = params.get('tab');
    if (tab === 'integrations') {
      setActiveTab('integrations');
    }
    if (squareResult === 'connected') {
      toast.success('Square POS connected successfully!');
      fetchSquareStatus();
      // Clean URL
      window.history.replaceState({}, '', '/settings');
    } else if (squareResult === 'error') {
      toast.error('Failed to connect Square POS. Please try again.');
      window.history.replaceState({}, '', '/settings');
    }
  }, [fetchSquareStatus]);

  const handleSquareConnect = async () => {
    setSquareConnecting(true);
    try {
      const res = await squareApi.connect();
      const { authUrl } = res.data.data;
      // Redirect to Square OAuth
      window.location.href = authUrl;
    } catch {
      toast.error('Failed to initiate Square connection. Please try again.');
      setSquareConnecting(false);
    }
  };

  const handleSquareDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Square POS? Transaction syncing will stop.')) {
      return;
    }
    try {
      await squareApi.disconnect();
      setSquareStatus({ connected: false });
      toast.success('Square POS disconnected.');
    } catch {
      toast.error('Failed to disconnect Square.');
    }
  };

  const handleSquareSyncLocations = async () => {
    setSquareSyncing(true);
    try {
      await squareApi.syncLocations();
      await fetchSquareStatus();
      toast.success('Square locations synced!');
    } catch {
      toast.error('Failed to sync locations.');
    } finally {
      setSquareSyncing(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const updateData: Parameters<typeof businessApi.updateProfile>[0] = {
        businessName: profile.businessName,
        category: profile.category,
        description: profile.description,
      };
      if (profile.phone) updateData.phoneNumber = profile.phone;
      if (profile.website) updateData.website = profile.website;
      await businessApi.updateProfile(updateData);
      toast.success('Business profile saved!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotifications = () => {
    try {
      localStorage.setItem('notificationSettings', JSON.stringify(notifications));
      toast.success('Notification settings saved!');
    } catch (error) {
      console.error('Failed to save notifications:', error);
      toast.error('Failed to save notification settings.');
    }
  };

  const handleSaveRewards = () => {
    try {
      localStorage.setItem('rewardsConfig', JSON.stringify(rewardsConfig));
      toast.success('Rewards configuration saved!');
    } catch (error) {
      console.error('Failed to save rewards config:', error);
      toast.error('Failed to save rewards configuration.');
    }
  };

  const handleSaveCustomerData = () => {
    try {
      localStorage.setItem('customerDataFields', JSON.stringify(customerDataFields));
      localStorage.setItem('customFields', JSON.stringify(customFields));
      toast.success('Customer data settings saved!');
    } catch (error) {
      console.error('Failed to save customer data settings:', error);
      toast.error('Failed to save customer data settings.');
    }
  };

  const categories = [
    { value: 'CAFE', label: 'Café / Coffee Shop' },
    { value: 'RESTAURANT', label: 'Restaurant' },
    { value: 'RETAIL', label: 'Retail Store' },
    { value: 'GROCERY', label: 'Grocery Store' },
    { value: 'FITNESS', label: 'Fitness / Wellness' },
    { value: 'BEAUTY', label: 'Beauty / Salon' },
    { value: 'OTHER', label: 'Other' },
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Settings</h1>
          <p className="text-base mt-2 text-black-lighter">
            Manage your business profile and preferences
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="inline-flex flex-wrap rounded-lg p-1 gap-1 self-start bg-cream-100 border border-border-light">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'profile'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Business Profile
        </button>
        <button
          onClick={() => setActiveTab('rewards')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'rewards'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Rewards Config
        </button>
        <button
          onClick={() => setActiveTab('customer-data')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'customer-data'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Customer Data
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'notifications'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Notifications
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
            activeTab === 'integrations'
              ? 'bg-white text-black shadow-sm'
              : 'bg-transparent text-black-lighter hover:text-black'
          }`}
        >
          Integrations
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Business Information</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Update your business details and branding
              </p>
            </div>

            <div className="p-6 space-y-5">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Business Logo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-lg bg-cream-100 border-2 border-border-light flex items-center justify-center">
                    {profile.logo ? (
                      <img src={profile.logo} alt="Logo" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <span className="text-3xl">☕</span>
                    )}
                  </div>
                  <div>
                    <Button variant="secondary" size="sm">
                      Upload New Logo
                    </Button>
                    <p className="text-xs text-black-lighter mt-1">
                      Recommended: Square image, at least 400x400px
                    </p>
                  </div>
                </div>
              </div>

              <Input
                label="Business Name"
                value={profile.businessName}
                onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
                isRequired
              />

              <Input
                type="select"
                label="Business Category"
                value={profile.category}
                onChange={(e) => setProfile({ ...profile, category: e.target.value })}
                isRequired
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Input>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="tel"
                  label="Phone Number"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  isRequired
                />
                <Input
                  type="email"
                  label="Email Address"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  isRequired
                />
              </div>

              <Input
                type="url"
                label="Website (Optional)"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                placeholder="https://example.com"
              />

              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Business Description
                </label>
                <textarea
                  value={profile.description}
                  onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black placeholder-black-lighter focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                  placeholder="Tell customers about your business..."
                />
                <p className="text-xs text-black-lighter mt-1">
                  This appears in the customer mobile app
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-border-light">
              <Button
                variant="primary"
                size="md"
                onClick={handleSaveProfile}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card className="mt-6">
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-red-600">Danger Zone</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Irreversible and destructive actions
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                <div className="flex-1">
                  <p className="text-base font-semibold text-red-900">
                    Delete Business Account
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Permanently delete your business account, all locations, customer data, and transaction history.
                    This action cannot be undone.
                  </p>
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  className="ml-4 shrink-0 text-red-600 border-red-300 hover:bg-red-100"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to permanently delete your account? This action CANNOT be undone.')) {
                      if (window.confirm('All customer data, transactions, and settings will be permanently deleted. Continue?')) {
                        toast.info('Account deletion coming soon. Please contact support@rewardables.app for assistance.');
                      }
                    }
                  }}
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Email Notifications</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Choose what email updates you want to receive
              </p>
            </div>

            <div className="p-6 space-y-4">
              {[
                { key: 'emailNewCustomer' as const, label: 'New Customer Sign-ups', description: 'Get notified when a new customer joins your loyalty program' },
                { key: 'emailDailyReport' as const, label: 'Daily Reports', description: 'Receive a daily summary of transactions and customer activity' },
                { key: 'emailWeeklyReport' as const, label: 'Weekly Reports', description: 'Receive a weekly summary with trends and insights' },
                { key: 'emailLowStars' as const, label: 'Low Stars Balance Alert', description: 'Get notified when your Stars balance is running low' },
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-3 p-4 bg-cream-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={notifications[item.key]}
                    onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })}
                    className="mt-1 w-4 h-4 text-orange-400 border-border-light rounded focus:ring-orange-400"
                  />
                  <div className="flex-1">
                    <p className="text-base font-semibold text-black">{item.label}</p>
                    <p className="text-sm text-black-lighter mt-0.5">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="mt-6">
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">SMS Notifications</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Receive important alerts via text message
              </p>
            </div>

            <div className="p-6 space-y-4">
              {[
                { key: 'smsNewTransaction' as const, label: 'New Transactions', description: 'Get a text for every transaction (can be high volume)' },
                { key: 'smsLargeRedemption' as const, label: 'Large Redemptions', description: 'Get notified of redemptions over $50 in value' },
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-3 p-4 bg-cream-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={notifications[item.key]}
                    onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })}
                    className="mt-1 w-4 h-4 text-orange-400 border-border-light rounded focus:ring-orange-400"
                  />
                  <div className="flex-1">
                    <p className="text-base font-semibold text-black">{item.label}</p>
                    <p className="text-sm text-black-lighter mt-0.5">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-border-light">
              <Button
                variant="primary"
                size="md"
                onClick={handleSaveNotifications}
              >
                Save Notification Settings
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Rewards Configuration Tab */}
      {activeTab === 'rewards' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Rewards & Points Configuration</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Customize how customers earn and redeem Stars tokens
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Earning Rate */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Stars Earning Rate
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={rewardsConfig.b3trPerDollar}
                    onChange={(e) => setRewardsConfig({ ...rewardsConfig, b3trPerDollar: Number(e.target.value) })}
                    min="1"
                    max="100"
                    className="w-24 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <span className="text-base text-black-lighter">Stars per $1 spent</span>
                </div>
                <p className="text-xs text-black-lighter mt-2">
                  Most businesses set this between 5-15 Stars per dollar
                </p>
              </div>

              {/* Redemption Minimum */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Minimum Redemption Amount
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={rewardsConfig.redemptionMinimum}
                    onChange={(e) => setRewardsConfig({ ...rewardsConfig, redemptionMinimum: Number(e.target.value) })}
                    min="0"
                    step="50"
                    className="w-32 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <span className="text-base text-black-lighter">Stars minimum to redeem</span>
                </div>
                <p className="text-xs text-black-lighter mt-2">
                  Set to 0 for no minimum, or require customers to accumulate points
                </p>
              </div>

              {/* Point Expiration */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Point Expiration
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={rewardsConfig.pointExpiration}
                    onChange={(e) => setRewardsConfig({ ...rewardsConfig, pointExpiration: Number(e.target.value) })}
                    min="0"
                    step="30"
                    className="w-32 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <span className="text-base text-black-lighter">days (0 = never expires)</span>
                </div>
                <p className="text-xs text-black-lighter mt-2">
                  Encourage redemption by setting expiration, or set to 0 for no expiration
                </p>
              </div>

              {/* Referral Bonus */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Referral Bonus
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={rewardsConfig.referralBonus}
                    onChange={(e) => setRewardsConfig({ ...rewardsConfig, referralBonus: Number(e.target.value) })}
                    min="0"
                    step="10"
                    className="w-32 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <span className="text-base text-black-lighter">Stars for each successful referral</span>
                </div>
                <p className="text-xs text-black-lighter mt-2">
                  Reward both referrer and new customer when someone joins via referral link
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-border-light">
              <Button
                variant="primary"
                size="md"
                onClick={handleSaveRewards}
              >
                Save Rewards Configuration
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Customer Data Collection Tab */}
      {activeTab === 'customer-data' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Customer Data Collection</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Configure what information to collect from customers during signup
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Standard Fields */}
              <div>
                <h4 className="text-base font-semibold text-black mb-3">Standard Fields</h4>
                <div className="space-y-3">
                  {customerDataFields.map((field) => (
                    <div key={field.id} className="flex items-center justify-between p-4 bg-cream-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={field.enabled}
                          onChange={(e) => {
                            if (!field.locked) {
                              setCustomerDataFields(customerDataFields.map(f =>
                                f.id === field.id ? { ...f, enabled: e.target.checked } : f
                              ));
                            }
                          }}
                          disabled={field.locked}
                          className="w-4 h-4 text-orange-400 border-border-light rounded focus:ring-orange-400 disabled:opacity-50"
                        />
                        <div>
                          <p className="text-base font-medium text-black flex items-center gap-2">
                            {field.name}
                            {field.locked && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded">
                                Required
                              </span>
                            )}
                          </p>
                          {!field.locked && (
                            <label className="flex items-center gap-2 mt-1">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => {
                                  setCustomerDataFields(customerDataFields.map(f =>
                                    f.id === field.id ? { ...f, required: e.target.checked } : f
                                  ));
                                }}
                                disabled={!field.enabled}
                                className="w-3 h-3 text-orange-400 border-border-light rounded focus:ring-orange-400"
                              />
                              <span className="text-xs text-black-lighter">Make required</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-base font-semibold text-black">Custom Fields</h4>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const newField = {
                        id: `custom_${Date.now()}`,
                        name: 'New Custom Field',
                        required: false,
                      };
                      setCustomFields([...customFields, newField]);
                    }}
                  >
                    + Add Custom Field
                  </Button>
                </div>

                {customFields.length === 0 ? (
                  <div className="p-8 border-2 border-dashed border-border-light rounded-lg text-center">
                    <p className="text-base text-black-lighter">
                      No custom fields yet. Click "Add Custom Field" to create one.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customFields.map((field, index) => (
                      <div key={field.id} className="flex items-center gap-3 p-4 bg-cream-50 rounded-lg">
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => {
                            setCustomFields(customFields.map((f, i) =>
                              i === index ? { ...f, name: e.target.value } : f
                            ));
                          }}
                          className="flex-1 px-3 py-2 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                          placeholder="Field name..."
                        />
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => {
                              setCustomFields(customFields.map((f, i) =>
                                i === index ? { ...f, required: e.target.checked } : f
                              ));
                            }}
                            className="w-4 h-4 text-orange-400 border-border-light rounded focus:ring-orange-400"
                          />
                          <span className="text-sm text-black-lighter">Required</span>
                        </label>
                        <button
                          onClick={() => {
                            setCustomFields(customFields.filter((_, i) => i !== index));
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Privacy Notice */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Privacy & Compliance</p>
                    <p className="text-sm text-blue-800 mt-1">
                      All customer data is encrypted in transit and at rest. By collecting customer information, you agree to comply with GDPR, CCPA, and other relevant data protection regulations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border-light">
              <Button
                variant="primary"
                size="md"
                onClick={handleSaveCustomerData}
              >
                Save Customer Data Settings
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Connected Integrations</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Link third-party services to enhance your loyalty program
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Square POS */}
              <div className={`p-5 border-2 rounded-lg ${squareStatus.connected ? 'border-green-200 bg-green-50/30' : 'border-dashed border-border-light'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center shrink-0">
                      <span className="text-white text-xl font-bold">S</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-black">Square POS</h4>
                      <p className="text-sm text-black-lighter mt-1">
                        Automatically sync transactions from your Square point-of-sale system
                      </p>
                      {squareLoading ? (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-black-lighter">Checking connection...</span>
                        </div>
                      ) : squareStatus.connected ? (
                        <>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              Connected
                            </span>
                            {squareStatus.merchantId && (
                              <span className="text-xs text-black-lighter">
                                Merchant: {squareStatus.merchantId}
                              </span>
                            )}
                          </div>
                          {squareStatus.lastSync && (
                            <p className="text-xs text-black-lighter mt-1">
                              Last synced: {new Date(squareStatus.lastSync).toLocaleString()}
                            </p>
                          )}
                          {squareStatus.locations && squareStatus.locations.length > 0 && (
                            <div className="mt-3 space-y-1">
                              <p className="text-xs font-semibold text-black">
                                Synced Locations ({squareStatus.locations.length}):
                              </p>
                              {squareStatus.locations.map((loc) => (
                                <div key={loc.id} className="text-xs text-black-lighter pl-2 border-l-2 border-green-300">
                                  {loc.name} — {loc.address}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-1 bg-cream-200 text-black-light text-xs font-semibold rounded">
                            Not Connected
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 ml-4">
                    {squareStatus.connected ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleSquareSyncLocations}
                          disabled={squareSyncing}
                        >
                          {squareSyncing ? 'Syncing...' : 'Sync Locations'}
                        </Button>
                        <Button
                          variant="tertiary"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={handleSquareDisconnect}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSquareConnect}
                        disabled={squareConnecting}
                      >
                        {squareConnecting ? 'Connecting...' : 'Connect'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Square Loyalty Sync */}
              {squareStatus.connected && (
                <div className="p-5 border-2 rounded-lg border-gray-200 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
                        <span className="text-white text-xl font-bold">L</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base font-semibold text-black">Square Loyalty</h4>
                          {loyaltyLoading ? (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded">
                              Loading...
                            </span>
                          ) : loyaltyStatus?.enabled ? (
                            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded">
                              Not Enabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-black-lighter mt-1">
                          Sync your Square Loyalty program with Rewardables to unify customer points and rewards.
                        </p>

                        {/* Setup instructions if not enabled */}
                        {!loyaltyStatus?.enabled && !loyaltyLoading && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs font-semibold text-blue-900 mb-1">Setup Instructions:</p>
                            <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Open your Square Dashboard at squareup.com</li>
                              <li>Go to Loyalty &gt; Create a Loyalty Program</li>
                              <li>Configure your point earning rules and rewards</li>
                              <li>Come back here and click "Enable Loyalty Sync"</li>
                            </ol>
                          </div>
                        )}

                        {/* Status details when enabled */}
                        {loyaltyStatus?.enabled && (
                          <div className="mt-3 space-y-2">
                            {loyaltyStatus.programName && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-black-lighter">Program:</span>
                                <span className="font-semibold text-black">{loyaltyStatus.programName}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-black-lighter">Linked Accounts:</span>
                              <span className="font-semibold text-black">{loyaltyStatus.linkedAccountsCount}</span>
                            </div>
                            {loyaltyStatus.lastSyncTime && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-black-lighter">Last Sync:</span>
                                <span className="font-semibold text-black">
                                  {new Date(loyaltyStatus.lastSyncTime).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {loyaltyStatus.syncErrors && loyaltyStatus.syncErrors.length > 0 && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                <p className="text-xs font-semibold text-red-700 mb-1">
                                  Sync Errors ({loyaltyStatus.syncErrors.length}):
                                </p>
                                {loyaltyStatus.syncErrors.slice(0, 3).map((err, i) => (
                                  <p key={i} className="text-xs text-red-600">{err}</p>
                                ))}
                                {loyaltyStatus.syncErrors.length > 3 && (
                                  <p className="text-xs text-red-500 mt-1">
                                    ...and {loyaltyStatus.syncErrors.length - 3} more
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Collapsible Sync Health Panel */}
                        {loyaltyStatus?.enabled && (
                          <div className="mt-4">
                            <button
                              onClick={() => {
                                setShowSyncHealth(!showSyncHealth);
                                if (!showSyncHealth && !loyaltyHealth) {
                                  fetchLoyaltyHealth();
                                }
                              }}
                              className="flex items-center gap-2 text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
                            >
                              <svg
                                className={`w-4 h-4 transition-transform duration-200 ${showSyncHealth ? 'rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              Sync Health Details
                            </button>

                            {showSyncHealth && (
                              <div className="mt-3 space-y-4">
                                {loyaltyHealth ? (
                                  <>
                                    {/* 24h Stats */}
                                    <div className="grid grid-cols-3 gap-3">
                                      <div className="p-3 bg-cream-50 rounded-lg text-center">
                                        <p className="text-lg font-bold text-black">{loyaltyHealth.last24h.totalSyncs}</p>
                                        <p className="text-xs text-black-lighter">Total Syncs</p>
                                      </div>
                                      <div className="p-3 bg-cream-50 rounded-lg text-center">
                                        <p className="text-lg font-bold text-red-600">{loyaltyHealth.last24h.failedSyncs}</p>
                                        <p className="text-xs text-black-lighter">Failed</p>
                                      </div>
                                      <div className="p-3 bg-cream-50 rounded-lg text-center">
                                        <p className={`text-lg font-bold ${loyaltyHealth.last24h.successRate >= 90 ? 'text-green-600' : loyaltyHealth.last24h.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                                          {loyaltyHealth.last24h.successRate.toFixed(1)}%
                                        </p>
                                        <p className="text-xs text-black-lighter">Success Rate</p>
                                      </div>
                                    </div>

                                    {/* Active Redemptions */}
                                    <div className="flex items-center justify-between text-sm p-3 bg-green-50 rounded-lg">
                                      <span className="text-green-800 font-medium">Active Redemptions</span>
                                      <span className="font-bold text-green-900">{loyaltyHealth.activeRedemptions}</span>
                                    </div>

                                    {/* Recent Sync Log */}
                                    {loyaltyHealth.recentLogs && loyaltyHealth.recentLogs.length > 0 && (
                                      <div>
                                        <p className="text-xs font-semibold text-black mb-2">Recent Sync Log</p>
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                          <table className="min-w-full text-xs">
                                            <thead className="bg-gray-50">
                                              <tr>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Time</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Action</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Details</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {loyaltyHealth.recentLogs.slice(0, 10).map((log) => (
                                                <tr key={log.id} className="hover:bg-gray-50">
                                                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                                    {new Date(log.timestamp).toLocaleTimeString()}
                                                  </td>
                                                  <td className="px-3 py-2 text-gray-900">{log.action}</td>
                                                  <td className="px-3 py-2">
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                                      log.status === 'SUCCESS'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                    }`}>
                                                      {log.status}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-gray-500 truncate max-w-[150px]">
                                                    {log.details || '-'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-center py-4">
                                    <div className="animate-spin h-5 w-5 border-2 border-orange-400 border-t-transparent rounded-full mx-auto mb-2"></div>
                                    <p className="text-xs text-black-lighter">Loading health data...</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0 ml-4">
                      {loyaltyStatus?.enabled ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleSyncLoyaltyCustomers}
                          disabled={loyaltySyncing}
                        >
                          {loyaltySyncing ? 'Syncing...' : 'Sync All Customers'}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleEnableLoyaltySync}
                          disabled={loyaltyEnabling || loyaltyLoading}
                        >
                          {loyaltyEnabling ? 'Enabling...' : 'Enable Loyalty Sync'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mailchimp - Available */}
              <div className="p-5 border-2 border-dashed border-border-light rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cream-100 rounded-lg flex items-center justify-center shrink-0">
                      <span className="text-2xl">📧</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-black">Mailchimp</h4>
                      <p className="text-sm text-black-lighter mt-1">
                        Sync customer data and send targeted email campaigns
                      </p>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 bg-cream-200 text-black-light text-xs font-semibold rounded">
                          Not Connected
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button variant="primary" size="sm">
                    Connect
                  </Button>
                </div>
              </div>

              {/* Zapier - Available */}
              <div className="p-5 border-2 border-dashed border-border-light rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cream-100 rounded-lg flex items-center justify-center shrink-0">
                      <span className="text-2xl">⚡</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-black">Zapier</h4>
                      <p className="text-sm text-black-lighter mt-1">
                        Connect to 5,000+ apps with automated workflows
                      </p>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 bg-cream-200 text-black-light text-xs font-semibold rounded">
                          Not Connected
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button variant="primary" size="sm">
                    Connect
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
};

export default SettingsPage;
